import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { refreshAccessToken } from '../pages/Auth/helpers'
import { clearSession } from './auth'

/* ── single-flight refresh lock ────────────────────────────── */
let refreshPromise: Promise<string> | null = null

const doRefresh = async (refreshToken: string): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken(refreshToken).finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

  /* ── token expiry check (client-side) ─────────────────────── */
  const isTokenExpired = (token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      if (!payload.exp) return false
      return payload.exp * 1000 < Date.now()
    } catch {
      return true
    }
  }

  /* ── axios instance ───────────────────────────────────────── */
  export const authAxios = axios.create()

  /* ── request interceptor ──────────────────────────────────── */
  authAxios.interceptors.request.use(
    async (config: AxiosRequestConfig) => {
      config.headers['content-type'] = 'application/json'

      const accessToken = localStorage.getItem('access_token')
      const refreshToken = localStorage.getItem('refresh_token')

      if (!accessToken || !refreshToken) {
        return config
      }

      if (!isTokenExpired(accessToken)) {
        config.headers.authorization = `Bearer ${accessToken}`
        return config
      }

      // token expired → single-flight refresh
      try {
        const newToken = await doRefresh(refreshToken)
        config.headers.authorization = `Bearer ${newToken}`
      } catch {
        clearSession()
        window.location.href = '/auth/login'
      }

      return config
    },
    (err) => Promise.reject(err)
  )

  /* ── response interceptor (401 / 403) ────────────────────── */
  authAxios.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        clearSession()
        window.location.href = '/auth/login'
      }
      if (error.response?.status === 403) {
        window.location.href = '/forbidden'
      }
      return Promise.reject(error)
    }
  )
